import React from 'react';
import { Badge, Text, Tooltip } from '@mantine/core';
import {
    IconAlertTriangleFilled,
    IconCaretDownFilled,
    IconCaretUpFilled,
    IconChevronDown,
    IconChevronUp,
    IconCircleCheck,
    IconCircleX,
    IconClockHour8,
    IconDotsCircleHorizontal,
    IconBan,
    IconTimelineEvent,
} from '@tabler/icons-react';
import dayjs from 'dayjs';

import { Request } from '../../codegen/graphql';
import formatCurrency from '../../utility/formatCurrency';
import { getCurrencyFromRequest } from '../../utility/getProductCurrency';
import { renderSurveySourceBadges } from './SurveySourceBadge';

export const STATUS_MAPPER = {
    Created: 'New Request',
    PendingSupplier: 'Pending',
    Pending: 'Pending',
    ApprovedSupplier: 'Interim Price',
    Approved: 'Approved',
    Declined: 'Declined',
    default: 'New Request',
};

export const AUTO_DECLINED_STATUS_FILTER = 'Auto-declined';

export const STATUS_TO_BACKEND = {
    'New Request': ['Created'],
    'Interim Price': ['ApprovedSupplier'],
    Pending: ['Pending'],
    Approved: ['Approved', 'ApprovedSupplier'],
    Declined: ['Declined'],
    // 'Urgent' and 'Auto-declined' are smart filters handled separately
};

export function getBackendStatusFilter(selected: string[] | null) {
    if (!selected?.length || selected.includes('All')) return undefined;
    const filtered = selected.filter(
        s => s !== 'Urgent' && s !== AUTO_DECLINED_STATUS_FILTER,
    );
    const backendStatuses: string[] = [];
    filtered.forEach(displayStatus => {
        const backend =
            STATUS_TO_BACKEND[displayStatus as keyof typeof STATUS_TO_BACKEND];
        if (backend) backendStatuses.push(...backend);
    });
    return backendStatuses.length > 0
        ? Array.from(new Set(backendStatuses))
        : undefined;
}

export function getUrgentFilter(selected: string[] | null) {
    return selected?.includes('Urgent') ? true : undefined;
}

export function getAutoDeclinedFilter(selected: string[] | null) {
    return selected?.includes(AUTO_DECLINED_STATUS_FILTER) ? true : undefined;
}

const SCOPE_LABEL_TO_BACKEND: Record<string, string> = {
    Store: 'STORE',
    'Specific Stores': 'SPECIFIC_STORES',
    Zone: 'ZONE',
    STORE: 'STORE',
    SPECIFIC_STORES: 'SPECIFIC_STORES',
    ZONE: 'ZONE',
};

export function getBackendScopeFilter(selected: string[] | null) {
    if (!selected?.length || selected.includes('All')) {
        return undefined;
    }

    const mapped = selected
        .map(value => SCOPE_LABEL_TO_BACKEND[value])
        .filter((value): value is string => Boolean(value));

    return mapped.length > 0 ? mapped : undefined;
}

const SURVEY_LABEL_TO_BACKEND: Record<string, string> = {
    'From survey': 'From survey',
    'Not from survey': 'Not from survey',
};

export function getBackendSurveyFilter(selected: string[] | null) {
    if (!selected?.length || selected.includes('All')) {
        return undefined;
    }

    const mapped = selected
        .map(value => SURVEY_LABEL_TO_BACKEND[value])
        .filter((value): value is string => Boolean(value));

    return mapped.length > 0 ? mapped : undefined;
}

type StatusIconEntry = {
    icon: React.ReactNode;
    label: string;
};

export const statusIcon: Record<string, StatusIconEntry> = {
    'New Request': {
        icon: (
            <div className="rounded-full bg-primary/20 text-primary flex flex-1 items-center w-6 h-6 p-1 mx-auto">
                <IconTimelineEvent />
            </div>
        ),
        label: 'New Request',
    },
    'Interim Price': {
        icon: (
            <div className="rounded-full bg-purple-500/20 text-purple-500 flex flex-1 items-center w-6 h-6 p-1 mx-auto">
                <IconClockHour8 />
            </div>
        ),
        label: 'Interim Price',
    },
    Pending: {
        icon: (
            <div className="rounded-full bg-yellow-500/20 text-yellow-500 flex flex-1 items-center w-6 h-6 p-1 mx-auto">
                <IconDotsCircleHorizontal />
            </div>
        ),
        label: 'In Progress',
    },
    Approved: {
        icon: (
            <div className="rounded-full bg-green-500/20 text-green-500 flex flex-1 items-center w-6 h-6 p-1 mx-auto">
                <IconCircleCheck />
            </div>
        ),
        label: 'Approved',
    },
    Declined: {
        icon: (
            <div className="rounded-full bg-red-500/20 text-red-500 flex flex-1 items-center w-6 h-6 p-1 mx-auto">
                <IconCircleX />
            </div>
        ),
        label: 'Declined',
    },
    Urgent: {
        icon: (
            <div className="rounded-full bg-red-500/20 text-red-500 flex flex-1 items-center w-6 h-6 p-1 mx-auto">
                <IconAlertTriangleFilled />
            </div>
        ),
        label: 'Urgent (Less than 4 hours remaining)',
    },
    'Auto-declined': {
        icon: (
            <div className="rounded-full bg-orange-500/20 text-orange-500 flex flex-1 items-center w-6 h-6 p-1 mx-auto">
                <IconBan />
            </div>
        ),
        label: 'Auto-declined (restricted SKU/store)',
    },
};

type SapStatusIconEntry = {
    element: React.ReactNode;
    label: string;
};

export const sapStatusIcon: Record<string, SapStatusIconEntry> = {
    Success: {
        element: (
            <Badge color="green" size="sm" variant="light">
                Success
            </Badge>
        ),
        label: 'SAP Integration Successful',
    },
    Processing: {
        element: (
            <Badge color="yellow" size="sm" variant="light">
                Processing
            </Badge>
        ),
        label: 'SAP Integration Processing',
    },
    Failed: {
        element: (
            <Badge color="red" size="sm" variant="light">
                Failed
            </Badge>
        ),
        label: 'SAP Integration Failed',
    },
    'Some Failed': {
        element: (
            <Badge color="red" size="sm" variant="light">
                Some Failed
            </Badge>
        ),
        label: 'SAP Integration Partially Failed',
    },
    'N/A': {
        element: (
            <Badge color="gray" size="sm" variant="light">
                N/A
            </Badge>
        ),
        label: 'SAP Integration Status N/A',
    },
};

export function renderSku({ productSku, productDescription }: Request) {
    return (
        <div className="flex min-w-0 flex-col justify-center">
            <Text size="sm" className="capitalize text-primary">
                #{productSku?.replace(/^0+/, '') || 'N/A'}
            </Text>
            <Text
                size="sm"
                className="text-ellipsis overflow-hidden whitespace-nowrap"
            >
                {productDescription || 'N/A'}
            </Text>
        </div>
    );
}

export function renderStoreDetails({ storeNo, storeName }: Request) {
    const trimmedStoreNo = storeNo?.trim();
    if (!trimmedStoreNo) {
        return (
            <Text size="sm" c="dimmed">
                —
            </Text>
        );
    }
    return (
        <div className="flex min-w-0 max-w-full items-center gap-1">
            <Text size="sm" className="min-w-0 truncate capitalize">
                {storeName}
            </Text>
            <Badge size="sm" color="gray" variant="light" className="shrink-0">
                {trimmedStoreNo}
            </Badge>
        </div>
    );
}

export function renderProcurementManagers(request: Request) {
    const procurementManagerUser = request.procurementManager;
    const pmName = procurementManagerUser
        ? `${procurementManagerUser.firstName} ${procurementManagerUser.lastName}`
        : '--';
    return <div className="text-left">{pmName}</div>;
}

export function renderDueDate({ dueBy, completed }: Request) {
    const textColor =
        !completed && dayjs(dueBy).isBefore(dayjs().add(4, 'hours'))
            ? 'text-red-500'
            : '';
    return (
        <div className="flex flex-col justify-center">
            <Text size="sm" className={`${textColor}`}>
                {completed ? 'Completed' : `Due ${dayjs(dueBy).fromNow()}`}
            </Text>
        </div>
    );
}

export function renderIsFromSurvey(request: Request) {
    return renderSurveySourceBadges(request);
}

export function renderSapStatus({ sapOutcome }: Request) {
    if (!sapOutcome || sapOutcome === 'N/A') return null;
    const entry = sapStatusIcon[sapOutcome as keyof typeof sapStatusIcon];
    if (!entry) return null;
    return (
        <Tooltip
            label={entry.label}
            openDelay={100}
            classNames={{
                tooltip: 'bg-[#4D5E80] text-white rounded-md text-xs px-2 py-1',
            }}
        >
            <div>{entry.element}</div>
        </Tooltip>
    );
}

export function renderImplementedDate({ changedPriceValidFrom }: Request) {
    if (!changedPriceValidFrom) return null;
    return (
        <div className="flex flex-col">
            <Text size="sm">
                {dayjs(changedPriceValidFrom).format('MMM D, YYYY')}
            </Text>
        </div>
    );
}

export function renderImplementedPrice(request: Request) {
    if (typeof request.cashbuildPrice !== 'number') return null;
    const currency = getCurrencyFromRequest(request);
    return (
        <div className="flex flex-col">
            <Text size="sm">
                {formatCurrency(request.cashbuildPrice, currency)}
            </Text>
        </div>
    );
}

export function renderMargin(request: Request) {
    const current = request.currentMargin ?? 0;
    const proposed = request.proposedMargin ?? 0;
    const isMarginPositive = proposed > current;
    const isMarginEqual = Math.abs(proposed - current) < 0.01;
    let marginAction: string;
    if (isMarginPositive) {
        marginAction = 'Increase from current margin';
    } else if (isMarginEqual) {
        marginAction = 'No change from current margin';
    } else {
        marginAction = 'Decrease from current margin';
    }
    const formattedMargin = parseFloat(`${current}`).toFixed(2);
    const tooltipLabel = `${marginAction} (${formattedMargin}%)`;

    return (
        <div className="flex gap-1 items-center">
            <Tooltip
                label={`Benchmark Margin: ${request.benchmarkMargin}%`}
                openDelay={100}
                classNames={{
                    tooltip:
                        'bg-[#4D5E80] text-white rounded-md text-xs px-2 py-1',
                }}
            >
                <Text size="sm">{parseFloat(`${proposed}`).toFixed(2)}%</Text>
            </Tooltip>
            {isMarginEqual ? null : (
                <Tooltip
                    label={tooltipLabel}
                    openDelay={100}
                    classNames={{
                        tooltip:
                            'bg-[#4D5E80] text-white rounded-md text-xs px-2 py-1',
                    }}
                >
                    {isMarginPositive ? (
                        <IconCaretUpFilled
                            className="text-green-500"
                            size={16}
                        />
                    ) : (
                        <IconCaretDownFilled
                            className="text-red-500"
                            size={16}
                        />
                    )}
                </Tooltip>
            )}
        </div>
    );
}

function getRequestStatusLabel(request: Request): string {
    if (request.isUrgent) {
        return 'Urgent';
    }
    if (request.isAutoDeclined) {
        return AUTO_DECLINED_STATUS_FILTER;
    }
    return (
        STATUS_MAPPER[request.latestStatus as keyof typeof STATUS_MAPPER] ||
        'New Request'
    );
}

export function renderStatus(request: Request) {
    const requestStatus = getRequestStatusLabel(request);
    const entry = statusIcon[requestStatus as keyof typeof statusIcon];

    return (
        <Tooltip
            label={entry?.label}
            openDelay={100}
            classNames={{
                tooltip: 'bg-[#4D5E80] text-white rounded-md text-xs px-2 py-1',
            }}
        >
            <div>{entry?.icon}</div>
        </Tooltip>
    );
}

export function renderPriceDirection(request: Request) {
    const current = request.currentPrice ?? 0;
    const proposed = request.proposedPrice ?? 0;
    const delta = proposed - current;
    if (Math.abs(delta) < 0.01) {
        return (
            <Badge color="gray" variant="light">
                No change
            </Badge>
        );
    }
    const isIncrease = delta > 0;
    return (
        <Badge
            leftSection={
                isIncrease ? (
                    <IconChevronUp size={16} className="mt-1" />
                ) : (
                    <IconChevronDown size={16} className="mt-1" />
                )
            }
            color={isIncrease ? 'green' : 'orange'}
            variant="light"
        >
            {isIncrease ? 'Increase' : 'Decrease'}
        </Badge>
    );
}

export function formatPercent(value?: number | null): string {
    return `${parseFloat(`${value ?? 0}`).toFixed(2)}%`;
}
